import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

// Import scenario hooks - called unconditionally at the top level of this component
// to satisfy the Rules of Hooks. See VaccineCurrentDoseRow.jsx for the full
// architectural explanation of why these are separate components.
import { usePWPreviousLogicEnhanced } from "./scenarios/pregnant-women-previous";
import { useChildPreviousLogicEnhanced } from "./scenarios/children-previous";

/**
 * VaccinePreviousDoseRow
 *
 * Renders a single dose row in the Previous Vaccination tab (checkbox + date picker variant).
 *
 * WHY TWO HOOKS ARE CALLED UNCONDITIONALLY:
 * See VaccineCurrentDoseRow.jsx. Short version: hooks cannot be called inside if/else,
 * so both are called and the correct result is selected via a plain variable assignment.
 *
 * NOTE ON selectedDoses PROP:
 * This is the array of ALL doses for the parent vaccine (not just this one dose).
 * Both scenario hooks need this for cross-dose logic:
 *   - PW:    to know whether TD-1 has been selected so TD-2 can be enabled
 *   - Child: to compute frequency-adjusted minDate for dose N given dose N-1's date
 * The shape is: [{ selected: boolean, administeredDate: string|null }]
 *
 * NOTE ON formState SHAPE FOR PW HOOK:
 * usePWPreviousLogicEnhanced expects its fourth argument as `formState` where
 * shouldShowDatePicker checks `formState?.doses?.[doseIndex]?.selected`.
 * We therefore build a { doses: selectedDoses } wrapper to match that contract.
 * This is an internal adapter detail - it does not leak into the parent component.
 *
 * @param {Object}   vaccine               - Vaccine object
 * @param {Object}   dose                  - Individual dose object from vaccine.availableDoses
 * @param {number}   doseIndex             - Zero-based dose index
 * @param {string}   beneficiaryType       - 'PW' | 'CHILD'
 * @param {Object}   profileData           - Full profile: { lmc_date, vaccination } for PW,
 *                                           { dob } for CHILD
 * @param {Array}    administeredVaccinations - Vaccination history array
 * @param {Function} decryptFunction        - Stable decrypt function reference
 * @param {Array}    selectedDoses          - All dose states for this vaccine:
 *                                           [{ selected: bool, administeredDate: string|null }]
 * @param {Function} onDoseToggle           - (doseIndex, checked) => void
 * @param {Function} onDoseDate             - (doseIndex, dateString) => void
 */
const VaccinePreviousDoseRow = ({
  vaccine,
  dose,
  doseIndex,
  beneficiaryType,
  profileData,
  administeredVaccinations,
  decryptFunction,
  identityContext = null,
  previousVaccines = [],
  onDoseToggle,
  onDoseDate,
  forceDisabled = false,
  forceDisabledReason = "",
}) => {
  const dosesSnapshotKey = useMemo(
    () =>
      (vaccine.availableDoses || [])
        .map((d) => `${d.selected ? 1 : 0}:${d.administeredDate || ''}`)
        .join('|'),
    [vaccine.availableDoses]
  );

  const selectedDoses = useMemo(
    () =>
      (vaccine.availableDoses || []).map((d) => ({
        selected: d.selected || false,
        administeredDate: d.administeredDate || null,
      })),
    [dosesSnapshotKey, vaccine.availableDoses]
  );

  // ---- HOOK CALLS (must be unconditional and at top level) ----

  // PW hook: usePWPreviousLogicEnhanced(vaccine, profileData, decryptFunction, formState)
  // We wrap selectedDoses in { doses: selectedDoses } to match the formState contract
  // that the PW hook's shouldShowDatePicker() expects internally.
  // Memoized so the object reference is stable across renders where selectedDoses
  // hasn't changed. Without this, a new object literal would be created every render,
  // which would invalidate the useMemo inside usePWPreviousLogicEnhanced/allDoseConfigs
  // on every single render regardless of whether the actual data changed.
  const pwFormState = useMemo(
    () => ({ doses: selectedDoses || [] }),
    [selectedDoses]
  );
  const pwLogic = usePWPreviousLogicEnhanced(
    vaccine,
    profileData,   // full profileData object: { lmc_date, vaccination }
    decryptFunction,
    pwFormState,    // fourth arg = formState for cross-dose PW logic
    identityContext
  );

  // Child hook: useChildPreviousLogicEnhanced(vaccine, childDob, administeredDoses, decryptFunction, selectedDoses)
  // Child hook takes selectedDoses directly (not wrapped) because ChildPreviousRules
  // uses selectedDoses?.[doseIndex].administeredDate directly for frequency constraints.
  const childLogic = useChildPreviousLogicEnhanced(
    vaccine,
    profileData?.dob,          // DOB string for age calculation
    administeredVaccinations,  // vaccination history array
    decryptFunction,
    selectedDoses,             // array of { selected, administeredDate } for all doses
    previousVaccines,          // the entire previous_vaccines array for conflict checks
    identityContext
  );

  // ---- SELECT THE CORRECT LOGIC RESULT ----
  const previousLogic = beneficiaryType === "PW" ? pwLogic : childLogic;

  // ---- LOADING STATE ----
  if (!previousLogic.isReady) {
    return (
      <Box sx={{ py: 1 }}>
        <CircularProgress size={12} sx={{ mr: 1 }} />
        <Typography component="span" fontSize={12} color="text.disabled">
          Loading dose {doseIndex + 1}...
        </Typography>
      </Box>
    );
  }

  // ---- DOSE CONFIG ----
  const doseConfig = previousLogic.getDoseConfig(doseIndex);
  // getDateConstraints returns { minDate, maxDate, shouldDisableDate } ready for MUI DatePicker
  const dateConstraints = previousLogic.getDateConstraints(doseIndex);

  // ---- ALREADY ADMINISTERED: show date only, no checkbox ----
  // An administered dose is read-only. The user cannot change it here.
  if (doseConfig.status === "administered") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
          borderBottom:
            doseIndex < (vaccine.availableDoses?.length ?? 1) - 1
              ? "1px solid #eee"
              : "none",
        }}
      >
        <Typography
          fontSize={14}
          sx={{ fontWeight: 500, color: "text.primary" }}
        >
          {dose.doseName}
        </Typography>
        <Typography fontSize={12} sx={{ fontWeight: 500, color: "text.primary" }}>
          {doseConfig.date || "Updated"}
        </Typography>
      </Box>
    );
  }

  // ---- NOT YET ADMINISTERED: checkbox (possibly disabled) + conditional date picker ----
  // shouldShowDatePicker checks BOTH config.showDatePicker AND whether the user
  // has actually ticked this dose's checkbox. The hook handles that logic internally.
  const isDoseDisabled = forceDisabled || !doseConfig.checkboxEnabled;
  const showDatePicker = !forceDisabled && previousLogic.shouldShowDatePicker(doseIndex);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        py: 1,
        borderBottom:
          doseIndex < (vaccine.availableDoses?.length ?? 1) - 1
            ? "1px solid #eee"
            : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Checkbox: enabled/disabled is determined purely by the scenario hook,
            not by inline logic here. This is the whole point of the hooks architecture. */}
        {vaccine.availableDoses?.length > 1 ? (
          // Explicit flex Box guarantees checkbox and label are always inline.
          // FormControlLabel was previously used here but can inherit parent
          // flex-direction overrides from uwin.css, causing the checkbox to
          // stack above the dose name instead of sitting beside it.
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '120px' }}>
            <Checkbox
              size="small"
              checked={dose.selected || false}
              disabled={isDoseDisabled}
              onChange={(e) => onDoseToggle(doseIndex, e.target.checked)}
            />
            <Typography fontSize={14} color={isDoseDisabled ? "text.disabled" : "text.primary"}>
              {dose.doseName}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ minWidth: "120px", display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography fontSize={14} color={isDoseDisabled ? "text.disabled" : "text.primary"}>
              {dose.doseName}
            </Typography>
          </Box>
        )}

        {/* Date Picker: only shown when checkbox is ticked and hook says to show it */}
        {showDatePicker && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <DatePicker
              format="DD/MM/YYYY"
              value={dose.administeredDate ? dayjs(dose.administeredDate) : null}
              onChange={(newValue) => {
                if (!newValue || !newValue.isValid()) {
                  return;
                }

                const min = dateConstraints.minDate
                  ? dayjs(dateConstraints.minDate)
                  : null;
                const max = dateConstraints.maxDate
                  ? dayjs(dateConstraints.maxDate)
                  : null;

                if (
                  min?.isValid() &&
                  newValue.isBefore(min, "day")
                ) {
                  return;
                }

                if (
                  max?.isValid() &&
                  newValue.isAfter(max, "day")
                ) {
                  return;
                }

                if (
                  dateConstraints.shouldDisableDate &&
                  dateConstraints.shouldDisableDate(newValue)
                ) {
                  return;
                }

                onDoseDate(doseIndex, newValue.format("YYYY-MM-DD"));
              }}
              shouldDisableDate={dateConstraints.shouldDisableDate}
              minDate={
                dateConstraints.minDate ? dayjs(dateConstraints.minDate) : undefined
              }
              maxDate={
                dateConstraints.maxDate ? dayjs(dateConstraints.maxDate) : undefined
              }
              slotProps={{
                field: {
                  clearable: false,
                  onKeyDown: (event) => {
                    if (
                      event.key === "Backspace" ||
                      event.key === "Delete" ||
                      (event.key.length === 1 && !event.ctrlKey && !event.metaKey)
                    ) {
                      event.preventDefault();
                    }
                  },
                  onPaste: (event) => event.preventDefault(),
                },
                textField: {
                  size: "small",
                  sx: {
                    minWidth: "150px",
                    "& .MuiInputBase-root": { fontSize: "12px" },
                    "& .MuiInputBase-input": { cursor: "pointer" },
                  },
                },
                openPickerButton: {
                  disabled: false,
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Helper text: shown when checkbox is disabled or there is a reason to display.
          The reason comes from the scenario hook, not from inline business logic. */}
      {(forceDisabledReason || doseConfig.reason) && (
        <Typography
          fontSize={11}
          color="warning.main"
          sx={{ pl: 5, fontStyle: "italic" }}
        >
          {forceDisabledReason || doseConfig.reason}
        </Typography>
      )}
    </Box>
  );
};

// React.memo prevents this component from re-rendering when the parent re-renders
// but none of the props it receives have actually changed. This is especially
// valuable here because VaccinePreviousDoseRow is rendered inside a .map() over
// vaccine doses – without memo, ALL dose rows re-render whenever any sibling's
// checkbox or date changes, even if only one row's data was affected.
export default React.memo(VaccinePreviousDoseRow, (prev, next) => {
  if (prev.doseIndex !== next.doseIndex) return false;
  if (prev.beneficiaryType !== next.beneficiaryType) return false;
  if (prev.forceDisabled !== next.forceDisabled) return false;
  if (prev.forceDisabledReason !== next.forceDisabledReason) return false;
  if (prev.vaccine.id !== next.vaccine.id) return false;

  const prevDose = prev.vaccine.availableDoses?.[prev.doseIndex];
  const nextDose = next.vaccine.availableDoses?.[next.doseIndex];
  if (prevDose?.selected !== nextDose?.selected) return false;
  if (prevDose?.administeredDate !== nextDose?.administeredDate) return false;
  if (prevDose?.doseName !== nextDose?.doseName) return false;

  const prevKey = (prev.vaccine.availableDoses || [])
    .map((d) => `${d.selected ? 1 : 0}:${d.administeredDate || ''}`)
    .join('|');
  const nextKey = (next.vaccine.availableDoses || [])
    .map((d) => `${d.selected ? 1 : 0}:${d.administeredDate || ''}`)
    .join('|');
  if (prevKey !== nextKey) return false;

  return true;
});
